import { useId } from "react";
import { useTranslation } from "react-i18next";
import { FieldInput } from "@/features/studio/theme";
import { FieldGroupLabel, FieldHint } from "./field-label";

/** A budget range as typed, in lakh. Empty strings mean "not given". */
export interface BudgetRangeValue {
	minLakh: string;
	maxLakh: string;
}

export const EMPTY_BUDGET: BudgetRangeValue = { minLakh: "", maxLakh: "" };

const RUPEES_PER_LAKH = 100_000;
/**
 * The largest budget the form takes, in lakh. The API's integer price columns
 * stop at 2,147,483,647 rupees (21,474.8 lakh); a whole-lakh ceiling keeps the
 * message that names it exact.
 */
const MAX_BUDGET_LAKH = 21_474;

/** True when a typed figure is not blank and not a usable lakh amount. */
function isOutOfRange(lakh: string): boolean {
	const trimmed = lakh.trim();
	if (!trimmed) return false;
	const value = Number(trimmed);
	return !Number.isFinite(value) || value < 0 || value > MAX_BUDGET_LAKH;
}

function toRupees(lakh: string): number | null {
	const trimmed = lakh.trim();
	if (!trimmed) return null;
	const value = Number(trimmed);
	return Number.isFinite(value) && value >= 0
		? Math.round(value * RUPEES_PER_LAKH)
		: null;
}

/** Stored rupees back to the lakh figure the inputs show. */
export function budgetFromRupees(
	min: number | null | undefined,
	max: number | null | undefined,
): BudgetRangeValue {
	const lakh = (rupees: number | null | undefined) =>
		rupees == null ? "" : String(rupees / RUPEES_PER_LAKH);
	return { minLakh: lakh(min), maxLakh: lakh(max) };
}

/**
 * The message key for a range that cannot be saved, or null. Both ends or
 * neither: the API takes them together.
 */
export function budgetErrorKey(budget: BudgetRangeValue): string | null {
	if (isOutOfRange(budget.minLakh) || isOutOfRange(budget.maxLakh)) {
		return "onboarding.fields.budgetOutOfRange";
	}
	const min = toRupees(budget.minLakh);
	const max = toRupees(budget.maxLakh);
	if ((min === null) !== (max === null)) {
		return "onboarding.fields.budgetBothRequired";
	}
	if (min !== null && max !== null && min > max) {
		return "onboarding.fields.budgetInvalid";
	}
	return null;
}

/** The range in rupees; both null when it was left empty. */
export function budgetPayload(budget: BudgetRangeValue): {
	priceRangeMin: number | null;
	priceRangeMax: number | null;
} {
	return {
		priceRangeMin: toRupees(budget.minLakh),
		priceRangeMax: toRupees(budget.maxLakh),
	};
}

export function BudgetRangeFields({
	label,
	value,
	onChange,
}: {
	label: string;
	value: BudgetRangeValue;
	onChange: (next: BudgetRangeValue) => void;
}) {
	const { t } = useTranslation();
	const minId = useId();
	const maxId = useId();
	const errorKey = budgetErrorKey(value);
	// "Both or neither" is only worth saying once the user has stopped typing
	// into one box; the min > max case is wrong the moment it is true.
	const showError = errorKey === "onboarding.fields.budgetInvalid";
	return (
		<fieldset className="min-w-0">
			<FieldGroupLabel as="legend">{label}</FieldGroupLabel>
			<div className="grid grid-cols-2 gap-4">
				<FieldInput
					id={minId}
					type="number"
					min={0}
					step="any"
					inputMode="decimal"
					label={t("onboarding.fields.budgetMin")}
					value={value.minLakh}
					onChange={(event) =>
						onChange({ ...value, minLakh: event.target.value })
					}
					placeholder={t("onboarding.fields.budgetMinPlaceholder")}
				/>
				<FieldInput
					id={maxId}
					type="number"
					min={0}
					step="any"
					inputMode="decimal"
					label={t("onboarding.fields.budgetMax")}
					value={value.maxLakh}
					onChange={(event) =>
						onChange({ ...value, maxLakh: event.target.value })
					}
					placeholder={t("onboarding.fields.budgetMaxPlaceholder")}
				/>
			</div>
			{showError ? (
				<FieldHint tone="error">
					{t("onboarding.fields.budgetInvalid")}
				</FieldHint>
			) : (
				<FieldHint>{t("onboarding.fields.budgetHint")}</FieldHint>
			)}
		</fieldset>
	);
}
